<script setup>
import {ref, watch, computed} from "vue";
import {useLanguageStore} from "@/pinia";
import axios from "@/axios";

const languageStore = useLanguageStore();

const props = defineProps({
  isUserDetailModal: Boolean,
  userId: String,
})

const emits = defineEmits([
  'set_isUserDetailModal',
  'refresh',
  'openEdit'
])

const openEditModal = () => {
  emits('openEdit', user.value);
  close();
}

// User data
const user = ref(null);
const loading = ref(false);

// Role display mapping
const roleDisplay = computed(() => {
  if (!user.value?.role) return '-';
  const roleMap = {
    'ADMIN': languageStore.get('admin'),
    'EDITOR': languageStore.get('editor'),
    'USER': languageStore.get('user')
  };
  return roleMap[user.value.role] || user.value.role;
});

// Format date
const formatDate = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Fetch user data
const fetchUserData = async () => {
  if (!props.userId) return;

  loading.value = true;
  try {
    const response = await axios.get(`/api/users/${props.userId}`);
    user.value = response.data;
  } catch (error) {
    console.error('Failed to fetch user:', error);
    user.value = null;
  } finally {
    loading.value = false;
  }
};

// Watch for modal open
watch(() => props.isUserDetailModal, (newVal) => {
  if (newVal && props.userId) {
    fetchUserData();
  }
});

watch(() => props.userId, (newVal) => {
  if (newVal && props.isUserDetailModal) {
    fetchUserData();
  }
});

const close = () => {
  user.value = null;
  emits('set_isUserDetailModal', false);
}
</script>

<template>
  <v-dialog
      :model-value="isUserDetailModal"
      @update:model-value="close"
      width="550"
  >
    <v-card>
      <v-card-title class="modal-header">
        {{ languageStore.get('userDetail') }}
      </v-card-title>

      <v-card-text class="modal-body">
        <v-progress-circular
            v-if="loading"
            indeterminate
            color="primary"
            class="d-flex mx-auto my-8"
        />

        <template v-else-if="user">
          <!-- 기본 정보 섹션 -->
          <div class="section-title">
            <v-icon size="small" class="mr-2">mdi-account</v-icon>
            {{ languageStore.get('basicInfo') }}
          </div>
          <v-table density="compact" class="info-table">
            <tbody>
              <tr>
                <td class="label-cell">{{ languageStore.get('userId') }}</td>
                <td class="value-cell">{{ user.userId }}</td>
              </tr>
              <tr>
                <td class="label-cell">{{ languageStore.get('name') }}</td>
                <td class="value-cell">{{ user.name || '-' }}</td>
              </tr>
              <tr>
                <td class="label-cell">{{ languageStore.get('email') }}</td>
                <td class="value-cell">{{ user.email || '-' }}</td>
              </tr>
              <tr>
                <td class="label-cell">{{ languageStore.get('phoneNumber') }}</td>
                <td class="value-cell">{{ user.phone || '-' }}</td>
              </tr>
            </tbody>
          </v-table>

          <!-- 계정 정보 섹션 -->
          <div class="section-title mt-4">
            <v-icon size="small" class="mr-2">mdi-shield-account</v-icon>
            {{ languageStore.get('accountInfo') }}
          </div>
          <v-table density="compact" class="info-table">
            <tbody>
              <tr>
                <td class="label-cell">{{ languageStore.get('role') }}</td>
                <td class="value-cell">
                  <v-chip
                      :color="user.role === 'ADMIN' ? 'error' : user.role === 'EDITOR' ? 'warning' : 'info'"
                      size="small"
                      variant="flat"
                  >
                    {{ roleDisplay }}
                  </v-chip>
                </td>
              </tr>
              <tr>
                <td class="label-cell">{{ languageStore.get('status') }}</td>
                <td class="value-cell">
                  <v-chip
                      :color="user.active ? 'success' : 'grey'"
                      size="small"
                      variant="flat"
                  >
                    {{ user.active ? languageStore.get('active') : languageStore.get('inactive') }}
                  </v-chip>
                </td>
              </tr>
            </tbody>
          </v-table>

          <!-- 등록/수정 정보 섹션 -->
          <div class="section-title mt-4">
            <v-icon size="small" class="mr-2">mdi-history</v-icon>
            {{ languageStore.get('historyInfo') }}
          </div>
          <v-table density="compact" class="info-table">
            <tbody>
              <tr>
                <td class="label-cell">{{ languageStore.get('registrationDate') }}</td>
                <td class="value-cell">{{ formatDate(user.regDt) }}</td>
              </tr>
              <tr>
                <td class="label-cell">{{ languageStore.get('createdBy') }}</td>
                <td class="value-cell">{{ user.regUserId || '-' }}</td>
              </tr>
              <tr>
                <td class="label-cell">{{ languageStore.get('modifiedDate') }}</td>
                <td class="value-cell">{{ formatDate(user.modDt) }}</td>
              </tr>
              <tr>
                <td class="label-cell">{{ languageStore.get('modifiedBy') }}</td>
                <td class="value-cell">{{ user.modUserId || '-' }}</td>
              </tr>
            </tbody>
          </v-table>
        </template>

        <template v-else>
          <div class="no-data">{{ languageStore.get('noData') }}</div>
        </template>
      </v-card-text>

      <v-card-actions class="modal-footer">
        <v-spacer/>
        <v-btn
            v-if="user"
            color="primary"
            variant="flat"
            @click="openEditModal"
        >
          {{ languageStore.get('edit') }}
        </v-btn>
        <v-btn
            variant="outlined"
            @click="close"
        >
          {{ languageStore.get('close') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<style scoped>
/* 모달 헤더 */
.modal-header {
  background-color: #1A3A5C !important;
  color: #FFFFFF !important;
  padding: 16px 24px;
  font-weight: 600;
  font-size: 18px;
}

/* 모달 바디 */
:deep(.v-card-text.modal-body) {
  padding: 24px !important;
}

/* 섹션 타이틀 */
.section-title {
  display: flex;
  align-items: center;
  font-weight: 600;
  font-size: 14px;
  color: #1A3A5C;
  padding: 8px 0;
  border-bottom: 2px solid #1A3A5C;
  margin-bottom: 8px;
}

/* 정보 테이블 */
.info-table {
  background: #f8f9fa;
  border-radius: 4px;
}

.info-table :deep(table) {
  border-collapse: collapse;
}

.info-table :deep(tr) {
  border-bottom: 1px solid #dee2e6;
}

.info-table :deep(tr:last-child) {
  border-bottom: none;
}

.label-cell {
  width: 140px;
  font-weight: 500;
  color: #495057;
  padding: 12px 16px !important;
  background: #e9ecef;
}

.value-cell {
  padding: 12px 16px !important;
  color: #212529;
}

/* 모달 푸터 */
:deep(.v-card-actions.modal-footer) {
  padding: 16px 24px !important;
  border-top: 1px solid #DEE2E6;
  gap: 8px;
}

.no-data {
  text-align: center;
  color: #6c757d;
  padding: 40px 0;
}
</style>
